import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/config/firebaseConfig";
import { isExpiredBuyerOffer } from "./buyerOfferState";

// Reconcile older records in the view, without rewriting historical data.
// New expiries update both buyer arrays atomically in the backend.
export function useBuyerVehicleLists(userData) {
  const idsKey = JSON.stringify([...new Set(userData?.wonVehicles || [])]);
  const [snapshot, setSnapshot] = useState({ key: "", expired: [], loading: true });

  useEffect(() => {
    const ids = JSON.parse(idsKey);
    let active = true;
    const expired = new Set();
    const pending = new Set();
    const subscriptions = new Set();
    const timers = new Set();
    const publish = () => {
      if (active) setSnapshot({ key: idsKey, expired: [...expired], loading: pending.size > 0 });
    };
    const chunks = [];
    for (let index = 0; index < ids.length; index += 30) {
      chunks.push(ids.slice(index, index + 30));
      pending.add(chunks.length - 1);
    }
    publish();
    const listen = (chunk, index, attempt = 1) => {
      if (!active) return;
      const unsubscribe = onSnapshot(query(collection(db, "USERTOFIN"), where("id", "in", chunk)), (result) => {
        if (!active) return;
        chunk.forEach(id => expired.delete(id));
        result.docs.forEach(doc => {
          if (isExpiredBuyerOffer(doc.data())) expired.add(doc.data().id || doc.id);
        });
        pending.delete(index);
        publish();
      }, () => {
        if (!active) return;
        if (attempt < 5) {
          const timer = setTimeout(() => { timers.delete(timer); listen(chunk, index, attempt + 1); }, 500 * 2 ** (attempt - 1));
          timers.add(timer);
        } else {
          pending.delete(index);
          publish();
        }
      });
      subscriptions.add(unsubscribe);
    };
    chunks.forEach((chunk, index) => listen(chunk, index));
    return () => {
      active = false;
      subscriptions.forEach(unsubscribe => unsubscribe());
      timers.forEach(clearTimeout);
    };
  }, [idsKey]);

  return useMemo(() => {
    const expired = snapshot.key === idsKey ? snapshot.expired : [];
    return {
      ...userData,
      wonVehicles: (userData?.wonVehicles || []).filter(id => !expired.includes(id)),
      lostVehicles: [...new Set([
        ...(userData?.lostVehicles || []).filter(id => !(userData?.wonVehicles || []).includes(id)),
        ...expired,
      ])],
      buyerListsLoading: JSON.parse(idsKey).length > 0 && (snapshot.key !== idsKey || snapshot.loading),
    };
  }, [userData, idsKey, snapshot]);
}
